import { useRef, type CSSProperties, type MouseEvent } from 'react';
import { useOutsidePress, type OutsidePressRefs } from './use-outside-press.ts';

/**
 * - `modal`: a scrim covers the page; pressing it only closes the popup.
 * - `click-through`: nothing covers the page; a press outside closes the popup
 *   and still reaches whatever was pressed.
 */
export type PopupMenuOverlayMode = 'modal' | 'click-through';

export type PopupMenuOverlayProps = {
  mode: PopupMenuOverlayMode;
  className: string;
  ariaLabel: string;
  insideRefs: OutsidePressRefs;
  onDismiss: (pressTarget: Node) => void;
  style?: CSSProperties;
};

/**
 * Closes a popup on presses outside it. Every outside-dismissal rule of the
 * popup menu lives here, so the popups only declare what counts as inside.
 */
export function PopupMenuOverlay({
  mode,
  className,
  ariaLabel,
  insideRefs,
  onDismiss,
  style,
}: PopupMenuOverlayProps): JSX.Element | null {
  const scrimRef = useRef<HTMLButtonElement | null>(null);
  const insideRefsWithScrim: OutsidePressRefs = [scrimRef, ...insideRefs];

  useOutsidePress({
    insideRefs: insideRefsWithScrim,
    onOutsidePress: onDismiss,
  });

  if (mode === 'click-through') {
    return null;
  }

  const handleScrimClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    onDismiss(event.currentTarget);
  };

  return (
    <button
      ref={scrimRef}
      type="button"
      aria-label={ariaLabel}
      className={className}
      style={style}
      onClick={handleScrimClick}
    />
  );
}
